import { Diagnostic } from "@fairfleet/geotab";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { uniq } from "lodash";
import { useGeotabClient } from "@/components/GeotabClientContext";

export function useDiagnosticsByIds(diagnosticIds?: string[], disabled?: boolean) {
  const ids = uniq(diagnosticIds);
  const queryClient = useQueryClient();
  const { geotabClient, activeSessionId } = useGeotabClient();
  return useQuery({
    queryKey: ["diagnostics", activeSessionId, ids],

    queryFn: async ({ signal }): Promise<Record<string, Diagnostic>> => {
      if (!ids || ids.length === 0) {
        return Promise.resolve({});
      }
      const cachedDiagnostics: Record<string, Diagnostic> = {};
      const idsToFetch: string[] = [];

      ids.forEach(id => {
        const cachedData = queryClient.getQueryData<Diagnostic>([
          "diagnostic",
          activeSessionId,
          id,
        ]);
        if (cachedData) {
          cachedDiagnostics[id] = cachedData;
        } else {
          idsToFetch.push(id);
        }
      });
      // If all diagnostics are cached, return them
      if (idsToFetch.length === 0) {
        return cachedDiagnostics;
      }
      const results = await geotabClient.get("Diagnostic", { ids: idsToFetch }, 5000, signal);
      const diagnosticsDict = results.reduce((acc, diagnostic) => {
        if (diagnostic) {
          acc[diagnostic.id] = diagnostic;
        }
        return acc;
      }, cachedDiagnostics);
      return diagnosticsDict;
    },
    placeholderData: keepPreviousData,
    enabled: !disabled && ids?.length > 0,
  });
}
